import { useEffect, useState } from 'react'
import { applyTheme, resolveTheme, subscribeToSystemTheme, type ThemeChoice, type ResolvedTheme } from '@app/lib/theme'

const STORAGE_KEY = 'codeflows-theme-choice'

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch { /* localStorage unavailable */ }
  return 'system'
}

let currentChoice: ThemeChoice = readChoice()
const listeners = new Set<(choice: ThemeChoice) => void>()
let unsubscribeSystem: (() => void) | null = null

function applyCurrent(): void {
  applyTheme(resolveTheme(currentChoice))
}

function ensureSystemSubscription(): void {
  if (currentChoice === 'system') {
    if (unsubscribeSystem) return
    unsubscribeSystem = subscribeToSystemTheme(() => applyCurrent())
  } else {
    unsubscribeSystem?.()
    unsubscribeSystem = null
  }
}

/**
 * Apply the persisted choice on boot. Call once from main.tsx before
 * the first render — keeps the FOUC short.
 */
export function bootTheme(): void {
  applyCurrent()
  ensureSystemSubscription()
}

export function getThemeChoice(): ThemeChoice {
  return currentChoice
}

export function setThemeChoice(choice: ThemeChoice): void {
  if (choice === currentChoice) return
  currentChoice = choice
  try { localStorage.setItem(STORAGE_KEY, choice) } catch { /* ignore */ }
  applyCurrent()
  ensureSystemSubscription()
  listeners.forEach((fn) => fn(choice))
}

export function useThemeChoice(): [ThemeChoice, ResolvedTheme, (next: ThemeChoice) => void] {
  const [choice, setChoice] = useState(currentChoice)
  useEffect(() => {
    const listener = (c: ThemeChoice) => setChoice(c)
    listeners.add(listener)
    return () => { listeners.delete(listener) }
  }, [])
  return [choice, resolveTheme(choice), setThemeChoice]
}
